import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, useAuth } from "../App";
import useSmartBack from "../hooks/useSmartBack";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Separator } from "../components/ui/separator";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  RadioGroup,
  RadioGroupItem,
} from "../components/ui/radio-group";
import {
  ShieldCheck,
  IdCard,
  Camera,
  Phone,
  Mail,
  CarFront,
  MapPin,
  Star,
  CalendarDays,
  Clock,
  Languages,
  Share2,
  Flag,
  ChevronLeft,
  Loader2,
  Check,
  Zap,
  MessageSquare,
  Sparkles,
  Gauge,
  Fuel,
  Users,
  Quote,
} from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import i18nClient from "../i18n";
import useSeo from "../hooks/useSeo";
import ProfileBadges from "../components/ProfileBadges";
import TrustScoreCard from "../components/TrustScoreCard";

const REPORT_REASON_KEYS = ["spam", "fraud", "harassment", "inappropriate", "fake_profile", "other"];
const getReportReasons = () =>
  REPORT_REASON_KEYS.map((value) => ({
    value,
    label:
      value === "other"
        ? i18nClient.t("publicProfile.reportReasonOther", { defaultValue: "Otro" })
        : i18nClient.t(`publicProfile.reportReasons.${value}`),
  }));

const CRITERIA_KEYS = ["cleanliness", "communication", "accuracy", "punctuality", "vehicle_condition"];
const criterionLabel = (k) =>
  i18nClient.t(`publicProfile.reviewCategories.${k}`, { defaultValue: k });

function VerifBadge({ active, icon: Icon, label }) {
  return (
    <div
      className={`flex items-center gap-2 px-3 py-2 rounded-xl border text-sm transition-colors ${
        active
          ? "border-emerald-200 bg-emerald-50 text-emerald-900"
          : "border-neutral-200 bg-neutral-50 text-neutral-400"
      }`}
      data-testid={`verif-badge-${label}`}
    >
      <Icon className={`w-4 h-4 ${active ? "text-emerald-600" : "text-neutral-300"}`} />
      <span className="font-medium">{label}</span>
      {active && <Check className="w-3.5 h-3.5 ml-auto text-emerald-600" />}
    </div>
  );
}

function StatPill({ icon: Icon, value, label, testid }) {
  if (value === null || value === undefined || value === "") return null;
  return (
    <div className="flex flex-col gap-1 p-3 rounded-xl border border-neutral-200 bg-white" data-testid={testid}>
      <span className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wide text-neutral-500 font-semibold">
        <Icon className="w-3.5 h-3.5" />
        {label}
      </span>
      <span className="font-heading font-bold text-lg text-[#111111] leading-none">
        {value}
      </span>
    </div>
  );
}

function VehicleMiniCard({ v }) {
  const photo = v.photos?.[0];
  return (
    <Link
      to={`/cars/${v.vehicle_id}`}
      data-testid={`profile-vehicle-${v.vehicle_id}`}
      className="group rounded-2xl overflow-hidden border border-neutral-200 bg-white hover:shadow-md hover:border-neutral-300 transition-all"
    >
      <div className="aspect-[16/10] bg-neutral-100 relative overflow-hidden">
        {photo ? (
          <img
            src={photo}
            alt={`${v.make} ${v.model}`}
            loading="lazy"
            className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-neutral-300">
            <CarFront className="w-10 h-10" />
          </div>
        )}
        {v.instant_book && (
          <span className="absolute top-2 left-2 inline-flex items-center gap-1 bg-white/95 text-[#111111] text-[10px] font-semibold px-2 py-1 rounded-full shadow-sm">
            <Zap className="w-3 h-3" />
            Reserva instantánea
          </span>
        )}
      </div>
      <div className="p-3">
        <div className="flex items-baseline justify-between gap-2">
          <p className="font-heading font-semibold text-[15px] text-[#111111] truncate">
            {v.make} {v.model} {v.year}
          </p>
          {typeof v.average_rating === "number" && (
            <span className="inline-flex items-center gap-1 text-xs font-semibold text-neutral-700 shrink-0">
              <Star className="w-3 h-3 fill-amber-400 text-amber-400" />
              {v.average_rating.toFixed(1)}
            </span>
          )}
        </div>
        <p className="text-xs text-neutral-500 mt-0.5 capitalize">
          {v.transmission} · {v.fuel_type}
        </p>
        {v.location_city && (
          <p className="text-xs text-neutral-500 mt-1 inline-flex items-center gap-1">
            <MapPin className="w-3 h-3" /> {v.location_city}
          </p>
        )}
        <p className="font-heading font-bold text-terracotta mt-2">
          ${v.daily_rate?.toLocaleString()}{" "}
          <span className="text-xs text-neutral-500 font-medium">{i18nClient.t("publicProfile.currencyPerDay")}</span>
        </p>
      </div>
    </Link>
  );
}

function CriteriaBars({ criteria }) {
  const items = Object.entries(criteria || {});
  if (!items.length) return null;
  return (
    <div className="space-y-2.5">
      {items.map(([k, v]) => (
        <div key={k} className="flex items-center gap-3">
          <span className="text-xs text-neutral-600 w-32 shrink-0">
            {criterionLabel(k)}
          </span>
          <div className="flex-1 h-1.5 rounded-full bg-neutral-100 overflow-hidden">
            <div
              className="h-full bg-amber-400"
              style={{ width: `${Math.min(100, (v / 5) * 100)}%` }}
            />
          </div>
          <span className="text-xs font-semibold text-[#111111] tabular-nums w-8 text-right">
            {v.toFixed(1)}
          </span>
        </div>
      ))}
    </div>
  );
}

export default function PublicProfilePage() {
  const { t } = useTranslation();
  const { userId } = useParams();
  const navigate = useNavigate();
  // Public profiles are commonly deep-linked from search results, social
  // shares, or push notifications, so `navigate(-1)` would often dead-end.
  const goBack = useSmartBack("/");
  const { user: me } = useAuth();
  const [profile, setProfile] = useState(null);
  const [reviews, setReviews] = useState({ reviews: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [reportOpen, setReportOpen] = useState(false);
  const [reportReason, setReportReason] = useState("spam");
  const [reportDetail, setReportDetail] = useState("");
  const [reportSubmitting, setReportSubmitting] = useState(false);

  useSeo({
    title: profile?.name
      ? `${profile.name} · ${t("publicProfile.titleSuffix")}`
      : t("publicProfile.profileFallback"),
    description: profile?.name
      ? t("publicProfile.descWithName", { name: profile.name, defaultValue: `Perfil público de ${profile.name} en Rivones. Verificaciones, reseñas y vehículos disponibles.` })
      : t("publicProfile.descFallback"),
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [pRes, rRes] = await Promise.all([
          api.get(`/users/${userId}/profile`),
          api.get(`/reviews/user/${userId}`, { params: { limit: 20 } }),
        ]);
        if (cancelled) return;
        setProfile(pRes.data);
        setReviews(rRes.data || { reviews: [], total: 0 });
      } catch (err) {
        toast.error(
          err.response?.data?.detail || t("publicProfile.loadError")
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userId, t]);

  const handleShare = async () => {
    const url = window.location.href;
    const shareData = {
      title: profile?.name
        ? t("publicProfile.shareTitle", { name: profile.name })
        : t("publicProfile.shareTitleFallback"),
      text: profile?.name
        ? t("publicProfile.shareTextWithName", { name: profile.name, defaultValue: `Mira el perfil de ${profile.name} en Rivones` })
        : t("publicProfile.shareText"),
      url,
    };
    try {
      if (navigator.share) {
        await navigator.share(shareData);
        return;
      }
    } catch {
      /* user dismissed */
    }
    try {
      await navigator.clipboard.writeText(url);
      toast.success(t("publicProfile.linkCopied"));
    } catch {
      toast.error(t("publicProfile.linkCopyError"));
    }
  };

  const handleSubmitReport = async () => {
    setReportSubmitting(true);
    try {
      await api.post(`/users/${userId}/report`, {
        reason: reportReason,
        detail: reportDetail.trim(),
      });
      toast.success(t("publicProfile.reportSent"));
      setReportOpen(false);
      setReportDetail("");
      setReportReason("spam");
    } catch (err) {
      toast.error(
        err.response?.data?.detail || t("publicProfile.reportError")
      );
    } finally {
      setReportSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-terracotta" />
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-6">
        <p className="text-lg text-neutral-700 mb-4">{t("publicProfile.notFound")}</p>
        <Button onClick={goBack} variant="outline">
          {t("auth.back")}
        </Button>
      </div>
    );
  }

  const isHost = profile.role === "host" || profile.role === "admin";
  const memberSince = profile.member_since
    ? new Date(profile.member_since).toLocaleDateString("es-MX", {
        month: "long",
        year: "numeric",
      })
    : null;
  const isSelf = me?.user_id === profile.user_id;

  return (
    <div className="bg-sand min-h-screen pb-12" data-testid="public-profile-page">
      {/* Top bar */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 pt-6">
        <button
          onClick={goBack}
          className="inline-flex items-center gap-1 text-sm text-neutral-600 hover:text-[#111111] transition-colors"
          data-testid="profile-back-btn"
        >
          <ChevronLeft className="w-4 h-4" />
          Volver
        </button>
      </div>

      {/* Hero */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 mt-4">
        <div className="bg-white rounded-3xl border border-neutral-200 p-5 sm:p-8">
          <div className="flex flex-col sm:flex-row gap-5 sm:gap-8 items-start">
            <Avatar className="w-24 h-24 sm:w-28 sm:h-28 border-4 border-white shadow-md ring-1 ring-neutral-200">
              <AvatarImage src={profile.picture} />
              <AvatarFallback className="text-2xl font-bold bg-terracotta text-white">
                {profile.name?.[0] || "U"}
              </AvatarFallback>
            </Avatar>

            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h1
                  className="font-heading font-bold text-3xl sm:text-4xl text-[#111111] tracking-tight"
                  data-testid="profile-name"
                >
                  {profile.name}
                </h1>
                {isHost && (
                  <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-100 border-0">
                    Anfitrión
                  </Badge>
                )}
              </div>
              <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-neutral-600">
                {memberSince && (
                  <span className="inline-flex items-center gap-1.5">
                    <CalendarDays className="w-3.5 h-3.5" />
                    Miembro desde {memberSince}
                  </span>
                )}
                {profile.city && (
                  <span className="inline-flex items-center gap-1.5">
                    <MapPin className="w-3.5 h-3.5" />
                    {profile.city}
                  </span>
                )}
                {profile.languages?.length > 0 && (
                  <span className="inline-flex items-center gap-1.5">
                    <Languages className="w-3.5 h-3.5" />
                    {profile.languages.join(", ")}
                  </span>
                )}
              </div>

              {profile.rating?.average != null && (
                <div className="mt-3 inline-flex items-center gap-2 bg-amber-50 border border-amber-200 px-3 py-1.5 rounded-full">
                  <Star className="w-4 h-4 fill-amber-400 text-amber-400" />
                  <span className="font-heading font-bold text-[#111111]">
                    {profile.rating.average.toFixed(1)}
                  </span>
                  <span className="text-xs text-amber-900/80">
                    ({profile.rating.total_reviews} reseñas)
                  </span>
                </div>
              )}

              {/* Achievement badges + Trust Score compact pill */}
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <ProfileBadges badges={profile.profile_badges} />
                {profile.trust_score && (
                  <TrustScoreCard trust={profile.trust_score} compact testid="public-trust-compact" />
                )}
              </div>

              {profile.bio && (
                <p className="mt-4 text-neutral-700 leading-relaxed text-sm sm:text-base">
                  {profile.bio}
                </p>
              )}
            </div>

            {/* Actions */}
            <div className="flex sm:flex-col gap-2 w-full sm:w-auto">
              <Button
                variant="outline"
                size="sm"
                onClick={handleShare}
                data-testid="profile-share-btn"
                className="rounded-full flex-1 sm:flex-none"
              >
                <Share2 className="w-4 h-4 mr-1.5" />
                Compartir
              </Button>
              {!isSelf && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setReportOpen(true)}
                  data-testid="profile-report-btn"
                  className="rounded-full flex-1 sm:flex-none text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                >
                  <Flag className="w-4 h-4 mr-1.5" />
                  Reportar
                </Button>
              )}
            </div>
          </div>

          {/* Stats grid */}
          <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-2.5">
            <StatPill
              icon={CarFront}
              value={profile.stats.completed_trips}
              label={t("publicProfile.stats.tripsCompleted")}
              testid="stat-trips"
            />
            {profile.stats.acceptance_rate != null && isHost && (
              <StatPill
                icon={Sparkles}
                value={`${profile.stats.acceptance_rate}%`}
                label={t("publicProfile.stats.acceptance")}
                testid="stat-acceptance"
              />
            )}
            {profile.stats.cancellation_rate != null && isHost && (
              <StatPill
                icon={Clock}
                value={`${profile.stats.cancellation_rate}%`}
                label={t("publicProfile.stats.cancellation")}
                testid="stat-cancellation"
              />
            )}
            {profile.stats.response_time_minutes != null && (
              <StatPill
                icon={MessageSquare}
                value={`~${profile.stats.response_time_minutes} min`}
                label={t("publicProfile.stats.responseTime")}
                testid="stat-response"
              />
            )}
          </div>
        </div>
      </section>

      {/* Trust Score detailed card */}
      {profile.trust_score && (
        <section className="max-w-5xl mx-auto px-4 sm:px-6 mt-6">
          <TrustScoreCard trust={profile.trust_score} testid="public-trust-score" />
        </section>
      )}

      {/* Verification */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 mt-6">
        <div className="bg-white rounded-3xl border border-neutral-200 p-5 sm:p-7">
          <div className="flex items-center gap-2 mb-4">
            <ShieldCheck className="w-5 h-5 text-emerald-600" />
            <h2 className="font-heading font-bold text-xl text-[#111111]">
              Verificaciones
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            <VerifBadge
              active={profile.badges.id_verified}
              icon={IdCard}
              label={t("publicProfile.stats.identity")}
            />
            <VerifBadge
              active={profile.badges.license_verified}
              icon={IdCard}
              label={t("publicProfile.stats.license")}
            />
            <VerifBadge
              active={profile.badges.selfie_verified}
              icon={Camera}
              label={t("publicProfile.stats.selfie")}
            />
            <VerifBadge
              active={profile.badges.phone_verified}
              icon={Phone}
              label={t("publicProfile.stats.phone")}
            />
            <VerifBadge
              active={profile.badges.email_verified}
              icon={Mail}
              label={t("publicProfile.stats.email")}
            />
            {isHost && (
              <VerifBadge
                active={profile.badges.vehicle_ownership_verified}
                icon={CarFront}
                label={t("publicProfile.stats.vehicleOwnership")}
              />
            )}
          </div>
        </div>
      </section>

      {/* Ratings detail */}
      {Object.keys(profile.rating?.criteria || {}).length > 0 && (
        <section className="max-w-5xl mx-auto px-4 sm:px-6 mt-6">
          <div className="bg-white rounded-3xl border border-neutral-200 p-5 sm:p-7">
            <div className="flex items-center gap-2 mb-4">
              <Gauge className="w-5 h-5 text-terracotta" />
              <h2 className="font-heading font-bold text-xl text-[#111111]">
                Calificación detallada
              </h2>
            </div>
            <CriteriaBars criteria={profile.rating.criteria} />
          </div>
        </section>
      )}

      {/* Vehicles */}
      {isHost && profile.vehicles?.length > 0 && (
        <section className="max-w-5xl mx-auto px-4 sm:px-6 mt-6">
          <div className="bg-white rounded-3xl border border-neutral-200 p-5 sm:p-7">
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-2">
                <CarFront className="w-5 h-5 text-[#111111]" />
                <h2 className="font-heading font-bold text-xl text-[#111111]">
                  Vehículos disponibles
                </h2>
                <Badge className="bg-neutral-100 text-neutral-700 hover:bg-neutral-100 border-0 ml-1">
                  {profile.vehicles.length}
                </Badge>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {profile.vehicles.map((v) => (
                <VehicleMiniCard key={v.vehicle_id} v={v} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Reviews */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 mt-6">
        <div className="bg-white rounded-3xl border border-neutral-200 p-5 sm:p-7">
          <div className="flex items-center gap-2 mb-5">
            <Star className="w-5 h-5 text-amber-500 fill-amber-400" />
            <h2 className="font-heading font-bold text-xl text-[#111111]">
              Reseñas
            </h2>
            <Badge className="bg-neutral-100 text-neutral-700 hover:bg-neutral-100 border-0 ml-1">
              {reviews.total || 0}
            </Badge>
          </div>

          {reviews.reviews?.length === 0 ? (
            <p className="text-sm text-neutral-500 py-6 text-center">
              Aún no hay reseñas publicadas.
            </p>
          ) : (
            <div className="space-y-5">
              {reviews.reviews.map((r, i) => (
                <article
                  key={r.review_id || i}
                  data-testid={`profile-review-${i}`}
                  className="border border-neutral-100 rounded-2xl p-4 sm:p-5"
                >
                  <header className="flex items-start gap-3">
                    <Avatar className="w-10 h-10 shrink-0">
                      <AvatarImage src={r.reviewer?.picture} />
                      <AvatarFallback className="bg-neutral-200 text-neutral-700">
                        {r.reviewer?.name?.[0] || "U"}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-[#111111] text-sm">
                        {r.reviewer?.name || t("publicProfile.anonymousUser")}
                      </p>
                      <p className="text-xs text-neutral-500">
                        {r.created_at
                          ? new Date(r.created_at).toLocaleDateString("es-MX", {
                              year: "numeric",
                              month: "long",
                            })
                          : ""}
                      </p>
                    </div>
                    <div className="inline-flex items-center gap-1 text-sm font-semibold">
                      {[1, 2, 3, 4, 5].map((s) => (
                        <Star
                          key={s}
                          className={`w-3.5 h-3.5 ${
                            s <= Math.round(r.rating || r.host_rating || 0)
                              ? "fill-amber-400 text-amber-400"
                              : "text-neutral-300"
                          }`}
                        />
                      ))}
                    </div>
                  </header>
                  {r.comment && (
                    <div className="mt-3 flex gap-2">
                      <Quote className="w-4 h-4 text-neutral-300 shrink-0 mt-0.5" />
                      <p className="text-sm text-neutral-700 leading-relaxed">
                        {r.comment}
                      </p>
                    </div>
                  )}
                </article>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Report dialog */}
      <Dialog open={reportOpen} onOpenChange={setReportOpen}>
        <DialogContent className="sm:max-w-md" data-testid="report-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Flag className="w-5 h-5 text-red-600" />
              Reportar usuario
            </DialogTitle>
            <DialogDescription>
              Tu reporte es confidencial. Nuestro equipo lo revisará lo antes
              posible.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-sm font-semibold mb-2 block">{t("publicProfile.reportReasonLabel")}</Label>
              <RadioGroup
                value={reportReason}
                onValueChange={setReportReason}
                className="space-y-1.5"
              >
                {getReportReasons().map((r) => (
                  <div key={r.value} className="flex items-center gap-2">
                    <RadioGroupItem
                      value={r.value}
                      id={`reason-${r.value}`}
                      data-testid={`report-reason-${r.value}`}
                    />
                    <Label
                      htmlFor={`reason-${r.value}`}
                      className="text-sm font-normal cursor-pointer"
                    >
                      {r.label}
                    </Label>
                  </div>
                ))}
              </RadioGroup>
            </div>
            <div>
              <Label
                htmlFor="report-detail"
                className="text-sm font-semibold mb-2 block"
              >
                Detalles (opcional)
              </Label>
              <Textarea
                id="report-detail"
                value={reportDetail}
                onChange={(e) => setReportDetail(e.target.value)}
                rows={4}
                placeholder={t("publicProfile.reportDetailsPlaceholder")}
                data-testid="report-detail-input"
                maxLength={1000}
              />
              <p className="text-[11px] text-neutral-500 mt-1 text-right">
                {reportDetail.length}/1000
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setReportOpen(false)}
              disabled={reportSubmitting}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleSubmitReport}
              disabled={reportSubmitting}
              data-testid="report-submit-btn"
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {reportSubmitting ? (
                <Loader2 className="w-4 h-4 animate-spin mr-1.5" />
              ) : null}
              Enviar reporte
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
